import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  ClockHistory,
  Coin,
  EyeFill,
  Search,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'

import Navbar from '../../components/navbar/navbar'
import { CustomButton } from '../../components/CustomButton'
import { CustomSpinner } from '../../components/CustomSpinner'
import PessoasService from '../../services/database/PessoasService'
import { formatPrice } from '../../utils/formatPrice'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'
import { useOrderTable } from '../../hooks/useOrderTable'

import { ModalConsultarSaldo } from './ModalConsultarSaldo'
import { ModalModificarSaldo } from './ModalModificarSaldo'
import { ModalHistoricoSaldo } from './ModalHistoricoSaldo'

import NoFoundPeopleSvg from '../../assets/illustration/no-found-people.svg'
import './styles.css'

const ITEMS_PER_PAGE = 10

export function ConsultarSaldo() {
  const [pessoas, setPessoas] = useState([])
  const [isLoadingPessoas, setIsLoadingPessoas] = useState(true)
  const [search, setSearch] = useState('')

  const [modalConsultarIsOpen, setModalConsultarIsOpen] = useState(false)
  const [modalModificarIsOpen, setModalModificarIsOpen] = useState(false)
  const [modalHistoricoIsOpen, setModalHistoricoIsOpen] = useState(false)
  const [selectedPessoa, setSelectedPessoa] = useState(null)

  const [currentPage, setCurrentPage] = useState(0)

  const modalModificarSaldoRef = useRef(null)
  const modalHistoricoSaldoRef = useRef(null)

  const {
    sortedArray: sortedPessoas,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(pessoas, 'saldo_atual')

  const pageCount = Math.ceil(sortedPessoas.length / ITEMS_PER_PAGE)

  const currentPeople = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE
    return sortedPessoas.slice(start, start + ITEMS_PER_PAGE)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortedPessoas, orderType.field, orderType.type])

  // Estatísticas
  const stats = useMemo(() => {
    const totalSaldo = pessoas.reduce((s, p) => s + (p.saldo_atual ?? 0), 0)
    const comSaldo = pessoas.filter((p) => (p.saldo_atual ?? 0) > 0).length
    const semSaldo = pessoas.filter((p) => (p.saldo_atual ?? 0) <= 0).length
    return { totalSaldo, comSaldo, semSaldo }
  }, [pessoas])

  async function loadPessoas() {
    setIsLoadingPessoas(true)
    try {
      const data = await PessoasService.getAll(true)
      setPessoas(data)
      setCurrentPage(0)
    } catch (error) {
      console.error(error)
      toast.error('Erro ao listar as pessoas com saldo!')
    } finally {
      setIsLoadingPessoas(false)
    }
  }

  async function handleSearch() {
    if (!search.trim()) return
    try {
      setIsLoadingPessoas(true)
      const data = await PessoasService.getBySearch({ search })
      setPessoas(data)
      setCurrentPage(0)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível a pesquisa por clientes!')
    } finally {
      setIsLoadingPessoas(false)
    }
  }

  function getPaginationItems() {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i)
    const items = []
    const delta = 2
    for (let i = 0; i < pageCount; i++) {
      if (i === 0 || i === pageCount - 1 || Math.abs(i - currentPage) <= delta) {
        items.push(i)
      } else if (Math.abs(i - currentPage) === delta + 1) {
        items.push('...')
      }
    }
    return items
  }

  useEffect(() => { loadPessoas() }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page" style={{ background: '#fff', padding: '1.5rem' }}>

        {/* Cabeçalho */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.25rem' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 2 }}>Consultar Saldo dos Clientes</h1>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
              {pessoas.length} cliente{pessoas.length !== 1 ? 's' : ''} encontrado{pessoas.length !== 1 ? 's' : ''}
            </p>
          </div>
          <CustomButton color="#b019ed" onClick={loadPessoas} isLoading={isLoadingPessoas} isLoadingText="Recarregando...">
            <ArrowClockwise /> Recarregar
          </CustomButton>
        </div>

        {/* Cards de estatísticas */}
        {!isLoadingPessoas && pessoas.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
            {[
              { label: 'Total de clientes', value: pessoas.length, color: '#185FA5' },
              { label: 'Saldo total em haver', value: formatPrice(stats.totalSaldo), color: '#3B6D11' },
              { label: 'Com saldo positivo', value: stats.comSaldo, color: '#BA7517' },
              { label: 'Sem saldo', value: stats.semSaldo, color: '#888' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#f7f7f8', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 600, color }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Barra de pesquisa */}
        <Row className="mb-3 g-2">
          <Col>
            <Form.Control
              placeholder="Pesquise pelo nome do cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && search.trim()) handleSearch() }}
              style={{ fontSize: 13 }}
            />
          </Col>
          <Col xs="auto" style={{ display: 'flex', gap: 8 }}>
            <CustomButton
              onClick={handleSearch}
              isLoading={isLoadingPessoas}
              isLoadingText="Pesquisando..."
              isDisabled={!search.trim()}
            >
              <Search /> Pesquisar
            </CustomButton>
            {search && (
              <CustomButton
                color="#888"
                onClick={() => { setSearch(''); loadPessoas() }}
              >
                ✕ Limpar
              </CustomButton>
            )}
          </Col>
        </Row>

        {/* Estados */}
        {isLoadingPessoas ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
            <CustomSpinner color="#006fff" />
          </div>
        ) : pessoas.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 0', gap: 16 }}>
            <img src={NoFoundPeopleSvg} style={{ width: 320, opacity: 0.7 }} alt="Sem dados" />
            <p style={{ color: '#888', fontSize: 14 }}>Nenhum cliente encontrado</p>
          </div>
        ) : (
          <>
            {/* Tabela */}
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed', minWidth: 600 }}>
                <colgroup>
                  <col style={{ width: 80 }} />
                  <col />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 180 }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f7f7f8', borderBottom: '2px solid #e8e8e8' }}>
                    <th style={thStyle()} onClick={() => handleOrganize('cod_pessoa')}>
                      <SortLabel label="ID" field="cod_pessoa" check={check} />
                    </th>
                    <th style={thStyle()} onClick={() => handleOrganize('pes_razao')}>
                      <SortLabel label="Nome" field="pes_razao" check={check} />
                    </th>
                    <th style={thStyle('right')} onClick={() => handleOrganize('saldo_atual')}>
                      <SortLabel label="Saldo Atual" field="saldo_atual" check={check} />
                    </th>
                    <th style={thStyle('center')}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPeople.map((pessoa, idx) => {
                    const saldo = pessoa.saldo_atual ?? 0
                    return (
                      <tr
                        key={pessoa.cod_pessoa}
                        style={{
                          background: idx % 2 === 0 ? '#fff' : '#fafafa',
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa' }}
                      >
                        <td style={tdStyle()}>
                          <span style={{ color: '#aaa', fontSize: 12 }}>#{pessoa.cod_pessoa}</span>
                        </td>
                        <td style={tdStyle()}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pessoa.pes_razao}>
                            {pessoa.pes_razao}
                          </span>
                        </td>
                        <td style={tdStyle('right')}>
                          <span style={{
                            fontWeight: 600,
                            color: saldo > 0 ? '#3B6D11' : saldo < 0 ? '#A32D2D' : '#888',
                          }}>
                            {formatPrice(saldo)}
                          </span>
                        </td>
                        <td style={tdStyle('center')}>
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <OverlayTrigger overlay={<Tooltip>Consultar Movimentação</Tooltip>}>
                              <ActionBtn color="#8A2BE2" onClick={() => { setSelectedPessoa(pessoa); setModalConsultarIsOpen(true) }}>
                                <EyeFill size={14} color="#8A2BE2" />
                              </ActionBtn>
                            </OverlayTrigger>

                            <OverlayTrigger overlay={<Tooltip>Modificar Saldo</Tooltip>}>
                              <ActionBtn color="#ea8d0d" onClick={() => { setSelectedPessoa(pessoa); setModalModificarIsOpen(true) }}>
                                <Coin size={14} color="#ea8d0d" />
                              </ActionBtn>
                            </OverlayTrigger>

                            <OverlayTrigger overlay={<Tooltip>Histórico de Saldo</Tooltip>}>
                              <ActionBtn color="#4169E1" onClick={() => { setSelectedPessoa(pessoa); setModalHistoricoIsOpen(true) }}>
                                <ClockHistory size={14} color="#4169E1" />
                              </ActionBtn>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#888' }}>
                  Mostrando {currentPage * ITEMS_PER_PAGE + 1}–{Math.min((currentPage + 1) * ITEMS_PER_PAGE, sortedPessoas.length)} de {sortedPessoas.length}
                </span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <PagBtn disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>‹ Anterior</PagBtn>
                  {getPaginationItems().map((item, idx) =>
                    item === '...'
                      ? <span key={`e${idx}`} style={{ padding: '0 6px', color: '#aaa', fontSize: 13 }}>…</span>
                      : <PagBtn key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>{item + 1}</PagBtn>
                  )}
                  <PagBtn disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => p + 1)}>Próxima ›</PagBtn>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modalConsultarIsOpen && (
        <ModalConsultarSaldo isOpen={modalConsultarIsOpen} pessoa={selectedPessoa} onClose={() => setModalConsultarIsOpen(false)} />
      )}
      {modalModificarIsOpen && (
        <ModalModificarSaldo ref={modalModificarSaldoRef} pessoa={selectedPessoa} loadPessoas={loadPessoas} isOpen={modalModificarIsOpen} onClose={() => setModalModificarIsOpen(false)} />
      )}
      {modalHistoricoIsOpen && (
        <ModalHistoricoSaldo ref={modalHistoricoSaldoRef} pessoa={selectedPessoa} isOpen={modalHistoricoIsOpen} onClose={() => setModalHistoricoIsOpen(false)} />
      )}
    </>
  )
}

// --- Helpers ---

function thStyle(align = 'left') {
  return {
    padding: '10px 14px',
    textAlign: align,
    fontSize: 11.5,
    fontWeight: 600,
    color: '#555',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
    letterSpacing: '0.02em',
  }
}

function tdStyle(align = 'left') {
  return {
    padding: '10px 14px',
    textAlign: align,
    fontSize: 13,
    color: '#222',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  }
}

function SortLabel({ label, field, check }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {label}
      <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1 }}>
        <CaretUp size={9} color={check(field, 'asc')} />
        <CaretDown size={9} color={check(field, 'desc')} />
      </span>
    </span>
  )
}

function ActionBtn({ children, color, onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 30,
        height: 30,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        border: `1px solid ${color}44`,
        background: hovered ? color : `${color}18`,
        color: hovered ? '#fff' : color,
        cursor: 'pointer',
        transition: 'all 0.15s',
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {React.cloneElement(children, { color: hovered ? '#fff' : color })}
    </button>
  )
}

function PagBtn({ children, active, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 10px',
        fontSize: 12,
        borderRadius: 6,
        border: '1px solid',
        borderColor: active ? '#0d6efd' : '#dee2e6',
        background: active ? '#0d6efd' : '#fff',
        color: active ? '#fff' : disabled ? '#aaa' : '#333',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: active ? 600 : 400,
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  )
}